import { useEffect } from "react";
import { Carrot } from "@/components/Carrot";
import { Reveal } from "@/components/Reveal";
import { SiteNav } from "@/components/SiteNav";
import { CallToAction } from "@/components/sections/CallToAction";
import { Footer } from "@/components/sections/Footer";

// Photos are served from /static (see build.ts copy + src/index.ts) so the
// pre-rendered HTML and the hydrated client emit identical <img src>.
type Member = {
  name: string;
  role: string;
  photo?: string;
  focus?: string;
  bio: string;
  placeholder?: boolean;
};

const FOUNDERS: Member[] = [
  {
    name: "Regina Shchepoteva",
    role: "Co-Founder & CEO",
    photo: "/static/regina_avatar.png",
    focus: "object-center",
    bio: "Regina spent five years inside the world Carrot On The Horse serves (as a vet assistant, groomer, and horse handler on a working ranch) before deciding to fix it. She leads operations, sales, and the equine ground truth behind every product decision, and she's the one in the barn aisle and at the events, talking to the people who'll actually use this. A horse owner with a psychology background, she builds for how barns really run, not how software wishes they did.",
  },
  {
    name: "Vadim Shchepotev",
    role: "Co-Founder & CTO",
    photo: "/static/vadim_avatar.png",
    focus: "object-center",
    bio: "Vadim leads the AI and engineering behind Carrot On The Horse, with 15 years building software and the last several focused on the hard part: getting AI to read messy, high-stakes documents and turn them into trustworthy records. His work spans multimodal extraction, retrieval and agentic workflows, and human-in-the-loop systems for sensitive health data. That is the exact stack that makes “import, not input” possible.",
  },
];

const BUILDERS: Member[] = [
  {
    name: "Andrew Y.",
    role: "Lead Engineer · Fullstack Architecture",
    photo: "/static/andrew.jpg",
    focus: "object-center",
    bio: "Andrew leads fullstack architecture at Carrot On The Horse, with 8+ years building high-performance software end to end. He's worked in fintech, where data has to be exact and the stakes are real, and built real-time dashboards over large, fast-moving datasets. That's the same problem shape as a barn's records: high volume, high stakes, no room to be sloppy. He pairs deep frontend craft with solid backend engineering, and brings AI directly into the dev lifecycle, from tooling to day-to-day workflow.",
  },
  {
    name: "Pavel P.",
    role: "Lead Designer · Mobile & Product UX",
    photo: "/static/pavel.jpeg",
    focus: "object-center",
    bio: "Pavel leads design at Carrot On The Horse, with 20+ years across UX/UI, product design, and art direction. He's designed mobile UX for AI workflows and informative dashboards over medical data, the same problem shape as COTH. His work is defined by precision and motion: interfaces that feel exact and alive in the hand, not just correct on a spec. That craft is what makes a phone-first tool something people reach for in motion, around animals and mud, which is the whole game here. Pavel makes adoption the design problem and owns the product's look and feel end to end.",
  },
];

const ADVISORS: Member[] = [
  {
    name: "Samantha VanSickle",
    role: "Advisor · Owner & Head Trainer, Starfire",
    photo: "/static/sam.jpg",
    focus: "object-[74%_22%]",
    bio: "Samantha is the owner and head trainer of Starfire Training Center, an internationally recognized Gypsy Vanner program that has set the breed's standard for training, breeding, and competition: multiple Hall of Fame horses, the first two Super Horses, and the most influential stallion in the breed. A board member and past president of the Gypsy Vanner Horse Society, she's built a career as a trainer, breeder, clinician, and mentor on ethical horsemanship. It started at age two, the first time her parents set her on a pony, and became a lifelong pursuit of the connection between horses and their people. She keeps Carrot On The Horse honest about how the industry actually runs.",
  },
];

function MemberCard({ member }: { member: Member }) {
  return (
    <article
      className={`group h-full rounded-2xl border bg-paper p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_44px_-26px_var(--carrot-deep)] sm:p-8 ${
        member.placeholder ? "border-dashed border-ink/20 hover:border-carrot/50" : "border-ink/10 hover:border-carrot/40"
      }`}
    >
      <div className="flex items-center gap-4">
        {member.placeholder ? (
          <span className="flex size-20 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-carrot/40 bg-carrot-soft/40">
            <Carrot className="size-9 opacity-60 transition-transform duration-300 group-hover:rotate-6" />
          </span>
        ) : !member.photo ? (
          <span className="flex size-20 shrink-0 items-center justify-center rounded-full bg-carrot-soft shadow-[0_8px_20px_-8px_rgba(120,60,10,0.4)] ring-2 ring-carrot/30">
            <Carrot className="size-9 transition-transform duration-300 group-hover:scale-105" />
          </span>
        ) : (
          <img
            src={member.photo}
            alt={member.name}
            width={800}
            height={800}
            loading="lazy"
            decoding="async"
            className={`size-20 shrink-0 rounded-full object-cover shadow-[0_8px_20px_-8px_rgba(120,60,10,0.4)] ring-2 ring-carrot/30 transition-transform duration-300 group-hover:scale-105 ${member.focus}`}
          />
        )}
        <div className="min-w-0">
          <h3 className="font-display text-xl font-bold tracking-tight text-ink">{member.name}</h3>
          <p className="mt-1 text-sm font-bold text-field-deep">{member.role}</p>
          {member.placeholder && (
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-field-soft px-2.5 py-0.5 text-[0.7rem] font-bold uppercase tracking-wide text-field-deep">
              Seat reserved
            </span>
          )}
        </div>
      </div>
      <p className={`mt-6 leading-relaxed ${member.placeholder ? "text-ink/55" : "text-ink/70"}`}>{member.bio}</p>
    </article>
  );
}

function Group({ label, members, start = 0 }: { label: string; members: Member[]; start?: number }) {
  return (
    <div className="mt-14">
      <Reveal>
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">{label}</h2>
      </Reveal>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {members.map((m, i) => (
          <Reveal key={m.name} delay={(start + i) * 90}>
            <MemberCard member={m} />
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function Team() {
  useEffect(() => {
    document.title = "Team | CarrotOnTheHorse";
  }, []);

  return (
    <div className="min-h-screen">
      <SiteNav current="/team" />
      <main>
        <section className="mx-auto max-w-6xl px-5 pt-16 sm:px-8 sm:pt-24">
          <Reveal className="max-w-2xl">
            <h1 className="text-4xl font-extrabold leading-[1.02] text-ink sm:text-5xl lg:text-6xl">
              Split between the barn and the <span className="text-field">build</span>.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-ink/70">
              Carrot On The Horse is a family startup, founded by Regina and Vadim, a wife-and-husband team.
              One side knows the smell of the aisle and how the work actually gets done; the other knows how
              to teach AI to read the mess it leaves behind. We're growing the team around that seam.
            </p>
          </Reveal>

          <Group label="Founders" members={FOUNDERS} start={0} />
          <Group label="Building it" members={BUILDERS} start={2} />
          <Group label="Advisors" members={ADVISORS} start={4} />
        </section>

        <div className="mt-24 sm:mt-28">
          <CallToAction />
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Team;
